import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  Grid
} from '@mui/material';
import {
  ArrowBack,
  Save,
  ShoppingCart,
  Description,
  Payment,
  Schedule
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { createApplication, ApplicationAPIError } from '../api/application';
import { ProductSetting } from '../types/owner';

// バリデーションスキーマ
const createApplicationSchema = z.object({
  name: z.string()
    .min(1, 'アプリケーション名は必須です')
    .min(3, 'アプリケーション名は3文字以上で入力してください')
    .max(100, 'アプリケーション名は100文字以内で入力してください'),
  description: z.string()
    .min(1, '説明は必須です')
    .min(10, '説明は10文字以上で入力してください')
    .max(500, '説明は500文字以内で入力してください'),
  version: z.string()
    .min(1, 'バージョンは必須です')
    .refine(val => /^\d+\.\d+\.\d+$/.test(val.trim()), {
      message: 'バージョンはx.y.z形式で入力してください（例: 1.0.0）'
    }),
});

type CreateApplicationForm = z.infer<typeof createApplicationSchema>;

interface LocationState {
  productSetting: ProductSetting;
}

export const ApplicationCreateFromProductPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const productSetting = (location.state as LocationState)?.productSetting;

  const { control, handleSubmit, formState: { errors } } = useForm<CreateApplicationForm>({
    resolver: zodResolver(createApplicationSchema),
    defaultValues: {
      name: `${productSetting?.name || ''} App`,
      description: `${productSetting?.description || ''} のためのライセンス管理アプリケーション`,
      version: '1.0.0',
    },
  });

  // 商品設定が渡されていない場合はリダイレクト
  if (!productSetting) {
    navigate('/applications');
    return null;
  }

  const onSubmit = async (data: CreateApplicationForm) => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const applicationData = {
        name: data.name,
        description: data.description,
        version: data.version,
        product_setting_id: productSetting.id,
        product_setting_name: productSetting.name,
      };

      const result = await createApplication(applicationData);

      // 成功メッセージを表示
      setSuccess('アプリケーションが正常に作成されました！');

      // 2秒後にアプリケーション一覧画面に遷移
      setTimeout(() => {
        navigate('/applications', {
          state: {
            message: 'アプリケーションが正常に作成されました',
            createdApplicationId: result.application_id
          }
        });
      }, 2000);

    } catch (err) {
      if (err instanceof ApplicationAPIError) {
        setError(err.message);
      } else {
        setError('アプリケーションの作成に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price: number | null): string => {
    if (price === null) return '-';
    return `¥${price.toLocaleString()}`;
  };

  const formatPaymentType = (type: number): string => {
    return type === 0 ? '単発決済' : '定額決済';
  };

  const formatInterval = (value: number, unit: string): string => {
    const unitMap: { [key: string]: string } = {
      day: '日',
      week: '週',
      month: 'ヶ月',
      year: '年'
    };
    return `${value}${unitMap[unit] || unit}`;
  };

  return (
    <Box maxWidth={900} margin="auto" padding={3}>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ marginBottom: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/applications');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          マイアプリケーション
        </Link>
        <Typography color="text.primary">新規作成</Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={1} marginBottom={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/applications')}
          variant="outlined"
          size="small"
        >
          戻る
        </Button>
        <Typography variant="h4">
          アプリケーション作成
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ marginBottom: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 商品設定情報 */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ padding: 2, position: 'sticky', top: 24 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart />
              選択された商品設定
            </Typography>

            <Card variant="outlined" sx={{ marginBottom: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {productSetting.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {productSetting.description}
                </Typography>

                <Box marginBottom={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    <Payment sx={{ fontSize: 16, marginRight: 0.5 }} />
                    料金体系
                  </Typography>
                  <Typography variant="body2">
                    {formatPaymentType(productSetting.payment_type)}
                  </Typography>

                  {productSetting.payment_type === 0 ? (
                    <Typography variant="body2">
                      価格: {formatPrice(productSetting.single_price)}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">
                        価格: {formatPrice(productSetting.cycle_price)} / {formatInterval(productSetting.cycle_interval_value, productSetting.cycle_interval_unit)}
                      </Typography>
                      {productSetting.trial_days > 0 && (
                        <Typography variant="body2">
                          <Schedule sx={{ fontSize: 16, marginRight: 0.5 }} />
                          試用期間: {productSetting.trial_days}日
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                <Divider sx={{ marginY: 1 }} />

                <Typography variant="caption" color="text.secondary">
                  商品ID: {productSetting.id} | プレフィックス: {productSetting.prefix}
                </Typography>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              この商品設定を使用してアプリケーションを作成します。
              作成後は、この商品の販売情報と連携してライセンス管理が行えます。
            </Alert>
          </Paper>
        </Grid>

        {/* アプリケーション作成フォーム */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description />
                アプリケーション情報
              </Typography>

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="アプリケーション名"
                      fullWidth
                      margin="normal"
                      error={!!errors.name}
                      helperText={errors.name?.message || 'ユーザーに表示されるアプリケーション名を入力してください'}
                      required
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="説明"
                      fullWidth
                      multiline
                      rows={4}
                      margin="normal"
                      error={!!errors.description}
                      helperText={errors.description?.message || 'アプリケーションの概要や用途を詳しく説明してください'}
                      required
                    />
                  )}
                />

                <Controller
                  name="version"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="バージョン"
                      fullWidth
                      margin="normal"
                      error={!!errors.version}
                      helperText={errors.version?.message || 'セマンティックバージョニング形式で入力（例: 1.0.0）'}
                      required
                      placeholder="1.0.0"
                    />
                  )}
                />

                <Box sx={{ marginTop: 4, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                    disabled={submitting}
                    sx={{ minWidth: 140 }}
                  >
                    {submitting ? '作成中...' : 'アプリケーション作成'}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/applications')}
                    disabled={submitting}
                  >
                    キャンセル
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 作成後の説明 */}
          <Card sx={{ marginTop: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                作成後の設定について
              </Typography>

              <Typography variant="body2" paragraph>
                アプリケーション作成後、以下の機能が利用可能になります：
              </Typography>

              <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
                <Typography component="li" variant="body2" sx={{ marginBottom: 1 }}>
                  <strong>ライセンス管理：</strong> サブスクライトの販売情報に基づいてライセンスを発行・管理
                </Typography>
                <Typography component="li" variant="body2" sx={{ marginBottom: 1 }}>
                  <strong>デバイス管理：</strong> アクティベーション済みデバイスの監視と管理
                </Typography>
                <Typography component="li" variant="body2" sx={{ marginBottom: 1 }}>
                  <strong>API連携：</strong> 内部APIキーを使用した外部システムとの連携
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>販売状況確認：</strong> サブスクライトの契約情報との同期
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};